import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FC } from 'react';
import StoreItemRow from './StoreItemRow';
import { MarketStore } from '../../../app/services/api/orderManagementClient';
import { useTranslation } from 'react-i18next';
import { Pill } from '../../../components/shared/Pill';
import { EmptyRows } from '../../../components/forms/EmptyRows';
import { EmptyTable } from '../../../components/shared/EmptyTable';
import LoadingWheel from '../../../components/ui/LoadingWheel';

type Props = {
  list: MarketStore[];
  isLoading: boolean;
  totalCount: number;
  onClear: () => void;
  onCreateNew: () => void;
  editBasePath?: string;
};

const StoresList: FC<Props> = ({
  list,
  isLoading,
  totalCount,
  onClear,
  onCreateNew,
  editBasePath,
}) => {
  const { t } = useTranslation('stores');

  if (totalCount === 0 && !isLoading) {
    return (
      <EmptyTable
        bodyRowHeight={40}
        headerHeight={45}
        text={t('stores')}
        searchTerm={''}
        onClear={onClear}
        onAdd={onCreateNew}
        onAddIsCreating={false}
        addItemText={t('createNew')}
        onAddIsDisabled={false}
        withPaginationShown={false}
      />
    );
  }

  return (
    <>
      <Box
        sx={{
          p: 5,
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        {/* Left: Title and Pill */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
          }}
        >
          <Typography
            variant="textMD"
            fontWeight={600}
          >
            {t('stores')}
          </Typography>
          {(totalCount ?? 0) > 0 && <Pill text={`${totalCount} ${t('stores')}`} />}
        </Box>
      </Box>
      
      {isLoading ? (
        <LoadingWheel />
      ) : (
        <TableContainer>
          <Table
            sx={{
              borderBottomLeftRadius: '12px',
              borderBottomRightRadius: '12px',
              minWidth: '1200px',
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('storeName')}
                </TableCell>
                <TableCell
                  sx={{
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('market')}
                </TableCell>
                <TableCell
                  sx={{
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('instance')}
                </TableCell>
                <TableCell
                  sx={{
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('inventoryEnabled')}
                </TableCell>
                <TableCell
                  sx={{
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('inventoryStartDate')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((row) => (
                <StoreItemRow
                  key={row.customerId}
                  item={row}
                  editBasePath={editBasePath}
                />
              ))}
              <EmptyRows
                numberOfCurrentRows={list.length}
                height={40}
              />
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export default StoresList;
